import { FC, Fragment, memo, useCallback, useEffect, useRef } from "react";
import { MapProps, MapsProps } from "./Maps.interface";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { getAddressByLocation, getCurrentLocation, getLocationByAddress } from "./helpers";

import { StyledMap } from "./Maps.styled";

const GOOGLE_API_KEY = 'AIzaSyAdZ7Dmwgj3C01NphzJa0HHYC6UD941dQg';

const Map: FC<MapProps> = ({ address, canPlaceMarker, minHeight, onAddressChanged }) => {
  const ref = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>();
  const infoWindow = useRef<google.maps.InfoWindow | null>();
  const marker = useRef<google.maps.Marker | null>();
  const zoom = 17;

  const setCurretLocation = () => {
    const get = async () => {
      const location = await getCurrentLocation();

      if (location && map) {
        map.current?.setCenter(location);
        google.maps.event.trigger(map, "resize");
      }
    }

    get();
  }

  const setLocationAddress = (code: string) => {
    const get = async () => {
      try {
      const location = await getLocationByAddress(code);
      
      if (location && map) {
        map.current?.setCenter(location);
        google.maps.event.trigger(map, "resize");
      }
       } catch {setCurretLocation()};
    }

    get();
  }

  const placeMarker = (latLng) => {
      if (!canPlaceMarker) return;

      infoWindow.current && infoWindow.current.close();
      infoWindow.current = new google.maps.InfoWindow({});
      //clear previous direction if have one
      //directionsDisplay && directionsDisplay.setMap(null);
      //put marker itself

      if (!marker.current || !map.current) return;

      marker.current.setMap(map.current );
      marker.current.setPosition(latLng);
      //save destination
      //gmapModule.destination = latLng;
  }

  const setLabelOnMap = (addressStr?: string) => {
    const str = `<div style="text-align: center; font-size: 90%;"><span class="addr">${addressStr}</span></div>`;

    if (!infoWindow.current || !map.current) return;

    infoWindow.current.setContent(str);
    infoWindow.current.open(map.current, marker.current);
}

  const onMapClicked = async (ev) => { 
    placeMarker(ev.latLng); 
    const formatted_address = await getAddressByLocation(ev.latLng);
    onAddressChanged?.(formatted_address)
    setLabelOnMap(formatted_address);
  }

  const onAutocompleteAddressChanged = useCallback((place: google.maps.places.PlaceResult) => {
    const latLng = (place.geometry && place.geometry.location) || {lat: 0, lng: 0};

    if(!map) return;
    //sometimes maps not loaded (blank screen)
    map.current?.setCenter(latLng);
    google.maps.event.trigger(map, "resize");
    map.current?.setCenter(latLng);

    placeMarker(latLng);

    onAddressChanged?.(place.formatted_address || '')
    setLabelOnMap(place.formatted_address);
    // if (markerPlaceCallback) {
    //     var addressObj = getGeocodeFormattedObject(place);
    //     markerPlaceCallback(addressObj);
    // }
  }, []);

  useEffect(() => {
    if (ref.current && !map.current) {

      const localMap = new window.google.maps.Map(ref.current, {
        // disableDefaultUI: true,
        fullscreenControl: false,
        mapTypeControl: false,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        panControl: false,
        streetViewControl: false,
        zoom,
        zoomControl: false,
      });

      if (canPlaceMarker)
        localMap.addListener('click', (ev) => { onMapClicked(ev) }) ;

      map.current = localMap;

      //setGeoCoder(new window.google.maps.Geocoder());

      infoWindow.current  = new google.maps.InfoWindow({});

      marker.current = new window.google.maps.Marker({map: localMap});

      const element = document.getElementsByName('gmaps')?.[0] as HTMLInputElement;

      if (element && canPlaceMarker) {

        const autocomplete = new google.maps.places.Autocomplete(document.getElementsByName('gmaps')?.[0] as HTMLInputElement,
          {types: ['geocode']});

        autocomplete.addListener('place_changed', function () {
          const place = autocomplete.getPlace();
          onAutocompleteAddressChanged(place);
        });
      }
    }
  }, [ref]);

  useEffect(() => {
    if (map.current) {
      if (address) {
        setLocationAddress(address)
      } else {
        setCurretLocation();
      }
    }
  }, [map.current])

  return <StyledMap ref={ref} id="map" minHeight={minHeight} />;
}

export const Maps: FC<MapsProps> = ({ minHeight, address, canPlaceMarker, onAddressChanged }) => {
  const renderMap = (status: Status) => {
    switch (status) {
      case Status.LOADING: {
        return <>Loading...</>
      }
      case Status.FAILURE: {
        return <>Error...</>
      }
    }

    return <Fragment />;
  };

  return (
    <Wrapper apiKey={GOOGLE_API_KEY} render={renderMap} libraries={['places']}>
      <Map address={address} canPlaceMarker={canPlaceMarker} minHeight={minHeight} onAddressChanged={onAddressChanged} />
    </Wrapper>
  );
}

export default memo(Maps);
